import React, { useEffect, useMemo, useState } from 'react'
import toast from 'react-hot-toast';
import Breadcrumb from '../../components/common/Breadcrumb'
import ReactDataTable from 'react-data-table-component';
import { IconBan, IconCheck, IconEye, IconChecks, IconAlertCircle } from '@tabler/icons-react';
import { useAuth } from '../../contexts/AuthContext';
import { useLeaveTypes } from '../../hooks/useLeaveTypes';
import { useLeaveRequests, useCreateLeaveRequest, useConfirmLeaveRequest, useApproveLeaveRequest, useRejectLeaveRequest, useDownloadLeaveReport } from '../../hooks/useLeaveRequests';
import { useMyLeaveBalance } from '../../hooks/useLeaveBalances';
import { useEmployees } from '../../hooks/useEmployees';
import { isLeaveHR } from '../../utils/reportPermissions';

// Components
import LeaveStats from './components/LeaveStats';
import AddLeaveModal from './components/AddLeaveModal';
import ApproveLeaveModal from './components/ApproveLeaveModal'; // We can reuse this for confirm too or make it generic
import RejectLeaveModal from './components/RejectLeaveModal';
import ViewLeaveDetailsModal from './components/ViewLeaveDetailsModal';
import ActionDropdown from '../../components/common/ActionDropdown';
import ReportDownloadButton from '../../components/common/ReportDownloadButton';

const AVATAR_COLORS = ['#4f46e5', '#0891b2', '#059669', '#d97706', '#dc2626', '#7c3aed', '#db2777'];

export default function Leave() {
    const { user } = useAuth();
    console.log('Current User Context:', user);
    const orgId = user?.orgId;

    // Fetch Data
    const { data: leaveTypesData, isLoading: isLoadingTypes } = useLeaveTypes(orgId);
    const { data: leaveRequestsData, isLoading: isLoadingRequests } = useLeaveRequests(orgId);
    const { data: leaveBalancesData, isLoading: isLoadingBalances } = useMyLeaveBalance(orgId);
    const currentEmployeeId = user?.employeeId || user?.employee_id;

    // Report-download capability: only rbacapp.hr_act (or superuser) can pick
    // an employee / download for the whole organization. Everyone else can
    // only download their own leave.
    const isHRForReports = isLeaveHR(user);
    const { data: allEmployees } = useEmployees(orgId, isHRForReports);
    const reportEmployees = isHRForReports ? (allEmployees || []) : [];
    const downloadLeaveReport = useDownloadLeaveReport();

    // Mutations
    const createLeaveRequest = useCreateLeaveRequest();
    const confirmLeaveRequest = useConfirmLeaveRequest();
    const approveLeaveRequest = useApproveLeaveRequest();
    const rejectLeaveRequest = useRejectLeaveRequest();

    const leaveTypes = leaveTypesData || [];
    const leaveRequests = leaveRequestsData || [];
    const leaveBalances = leaveBalancesData || [];

    const [addLeaveModal, setAddLeaveModal] = useState(false);
    const [actionModal, setActionModal] = useState({ isOpen: false, type: '', request: null });
    const [viewDetailsModal, setViewDetailsModal] = useState(false);
    const [selectedLeave, setSelectedLeave] = useState(null);
    const [actionComments, setActionComments] = useState('');

    // Form states
    const [selectedLeaveType, setSelectedLeaveType] = useState('');
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const [leaveReason, setLeaveReason] = useState('');
    const [totalDays, setTotalDays] = useState(0);

    // Filter states
    const [filterText, setFilterText] = useState('');
    const [resetPaginationToggle, setResetPaginationToggle] = useState(false);

    // Format date helper
    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    };

    // Get status badge
    const getStatusBadge = (status) => {
        const map = {
            pending: <span className='badge badge-warning'>Pending Line Manager</span>,
            confirmed: <span className='badge badge-info'>Pending HR</span>,
            approved: <span className='badge badge-success'>Approved</span>,
            rejected: <span className='badge badge-danger'>Rejected</span>,
        };
        return map[status] || <span className='badge badge-secondary'>{status}</span>;
    };

    const handleActionSubmit = () => {
        const { type, request } = actionModal;
        if (!request) return;

        const options = {
            onSuccess: () => {
                setActionModal({ isOpen: false, type: '', request: null });
                setActionComments('');
                toast.success(`Leave request ${type}ed successfully`);
            },
            onError: (err) => toast.error(`Error: ${err.message}`)
        };

        if (type === 'confirm') {
            const confirmPayload = {
                leave_type: request.leave_type,
                start_date: request.start_date,
                end_date: request.end_date,
                days_requested: request.days_requested
            };

            confirmLeaveRequest.mutate({
                id: request.id,
                orgId,
                data: confirmPayload // Match the key name we added to the hook
            }, options);
        } else if (type === 'approve') {
            approveLeaveRequest.mutate({ id: request.id, orgId }, options);
        } else if (type === 'reject') {
            if (!actionComments.trim()) {
                toast.error('Please provide a reason for rejection');
                return;
            }
            rejectLeaveRequest.mutate({
                id: request.id,
                orgId,
                data: { rejection_reason: actionComments }
            }, options);
        }
    };

    const openActionModal = (type, request) => {
        setActionModal({ isOpen: true, type, request });
        setActionComments('');
    };

    const handleSubmitLeave = (files = []) => {
        if (!selectedLeaveType || !fromDate || !toDate || !leaveReason) {
            toast.error('Please fill all required fields');
            return;
        }

        const formData = new FormData();
        formData.append('leave_type', selectedLeaveType);
        formData.append('start_date', fromDate);
        formData.append('end_date', toDate);
        formData.append('reason', leaveReason);
        formData.append('days_requested', totalDays);
        if (currentEmployeeId) {
            formData.append('employee', currentEmployeeId);
        }
        files.forEach(file => formData.append('attachments', file));

        createLeaveRequest.mutate({ data: formData, orgId }, {
            onSuccess: () => {
                toast.success('Leave request submitted successfully!');
                setAddLeaveModal(false);
                // Reset form
                setFromDate(''); setToDate(''); setLeaveReason('');
            },
            onError: (err) => {
                const errData = err?.response?.data;
                if (errData?.attachments) {
                    toast.error(errData.attachments[0]);
                } else if (errData?.days_requested) {
                    toast.error(errData.days_requested[0]);
                } else if (errData?.leave_type) {
                    toast.error(errData.leave_type[0]);
                } else {
                    toast.error('Failed: ' + err.message);
                }
            }
        });
    };

    // Date calc - count only working days (exclude weekends)
    useEffect(() => {
        if (fromDate && toDate) {
            const start = new Date(fromDate);
            const end = new Date(toDate);
            let workingDays = 0;

            // Loop through each day and count only weekdays
            for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
                const dayOfWeek = d.getDay();
                if (dayOfWeek !== 0 && dayOfWeek !== 6) {
                    workingDays++;
                }
            }

            setTotalDays(workingDays);
        } else {
            setTotalDays(0);
        }
    }, [fromDate, toDate]);

    const columns = [
        {
            name: 'Employee',
            selector: row => row.employeeName,
            sortable: true,
            cell: row => {
                const initials = row.employeeName
                    ? row.employeeName.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()
                    : '?';
                const bg = AVATAR_COLORS[(row.employeeName?.charCodeAt(0) || 0) % AVATAR_COLORS.length];
                return (
                    <div className='flex items-center gap-10'>
                        <div style={{ backgroundColor: bg }} className='w-[35px] h-[35px] rounded-full flex items-center justify-center text-white font-semibold text-sm flex-shrink-0'>
                            {initials}
                        </div>
                        <div>
                            <p className='font-medium text-sm'>{row.employeeName}</p>
                            <p className='text-xs text-text-secondary'>{row.employeeId}</p>
                        </div>
                    </div>
                );
            },
            width: "200px"
        },
        { name: 'Leave Type', selector: row => row.leaveTypeName, sortable: true, width: "150px" },
        { name: 'Duration', selector: row => `${row.days_requested} Days`, sortable: true, width: "100px", center: true },
        { name: 'Dates', selector: row => `${formatDate(row.start_date)} - ${formatDate(row.end_date)}`, width: "200px" },
        { name: 'Status', selector: row => row.status, format: row => getStatusBadge(row.status), sortable: true, width: "180px" },
        {
            name: 'Actions',
            cell: row => {
                const isMine = String(row.employee) === String(currentEmployeeId);
                const isPending = row.status === 'pending';
                const isConfirmed = row.status === 'confirmed';

                // Permission Checks
                // Manager: Can confirm if they are the assigned line manager
                const isLineManager = row.line_manager_id && String(row.line_manager_id) === String(currentEmployeeId);
                const canConfirm = isPending && isLineManager;

                // HR: Can approve if status is confirmed AND they have HR permissions
                // We use checking of permissions array manually or via helper if available
                const isHR = user?.is_superuser || user?.permissions?.includes('rbacapp.hr_act') || user?.is_staff;
                const canApprove = isConfirmed && isHR;

                // Reject: allowed for Manager (at pending) or HR (at confirmed)
                const canReject = (isPending && isLineManager) || (isConfirmed && isHR);

                return (
                    <ActionDropdown
                        menuItems={[
                            {
                                label: 'View Details',
                                icon: IconEye,
                                onClick: () => { setSelectedLeave(row); setViewDetailsModal(true); },
                                description: "View full leave request information"
                            },
                            ...(canConfirm ? [{
                                label: 'Confirm (Manager)',
                                icon: IconCheck,
                                onClick: () => openActionModal('confirm', row),
                                description: "Line manager verification"
                            }] : []),
                            ...(canApprove ? [{
                                label: 'Approve (HR)',
                                icon: IconChecks,
                                onClick: () => openActionModal('approve', row),
                                description: "Final HR approval"
                            }] : []),
                            ...(canReject ? [{
                                label: 'Reject Request',
                                icon: IconBan,
                                danger: true,
                                onClick: () => openActionModal('reject', row),
                                description: "Deny this leave request"
                            }] : [])
                        ]}
                    />
                );
            },
            width: "150px",
            center: true
        }
    ];

    // Format table data - scoped to current user's own leave requests
    const formattedLeaves = leaveRequests
        .filter(leave => {
            const empId = typeof leave.employee === 'object' ? leave.employee.id : leave.employee;
            return String(empId) === String(currentEmployeeId);
        })
        .map(leave => ({
            ...leave,
            employeeName: leave.employee_name || 'Unknown User',
            employeeId: leave.employee_id || '',
            leaveTypeName: leave.leave_type_name || 'Unknown Type',
        }));

    // Filter Logic
    const filteredItems = formattedLeaves.filter(item => {
        if (!filterText) return true;
        const search = filterText.toLowerCase();
        // Safe access helper
        const getStr = (val) => String(val || '').toLowerCase();

        return (
            getStr(item.leave_type_name).includes(search) ||
            getStr(item.status).includes(search) ||
            // Access nested or direct fields safely
            getStr(item.employee_name || item.employee?.user?.username).includes(search)
        );
    });

    const subHeaderComponent = useMemo(() => {
        const handleClear = () => {
            if (filterText) {
                setResetPaginationToggle(!resetPaginationToggle);
                setFilterText('');
            }
        };
        return (
            <div className='flex gap-5 w-full justify-end'>
                <div className='relative w-full max-w-[221px]'>
                    <input
                        type='text'
                        value={filterText}
                        onChange={e => setFilterText(e.target.value)}
                        placeholder='Search here...'
                        className='bg-bg-light border border-border w-full py-[6px] px-3 min-h-[40px] rounded-[4px] placeholder:text-text-secondary focus:outline-none'
                    />
                </div>
                <button onClick={handleClear} className="btn btn-danger">Clear</button>
            </div>
        );
    }, [filterText, resetPaginationToggle]);

    if (isLoadingRequests || isLoadingBalances) return <div className='p-20 text-center'>Loading...</div>;

    return (
        <div className='flex flex-col min-h-screen gap-20'>
            <Breadcrumb title="Leave Management" path="Leave / Requests" />

            {/* BALANCE CARDS */}
            <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-20 mb-30'>
                {leaveBalances.map((bal, idx) => (
                    <div key={idx} className='bg-bg-dark p-20 rounded shadow-sm border border-border relative overflow-hidden'>
                        <div className={`absolute top-0 right-0 w-20 h-20 transform translate-x-10 -translate-y-10 rotate-45 ${bal.is_overdrawn ? 'bg-red-500' : 'bg-green-500'}`}></div>
                        <p className='text-xs text-text-secondary uppercase font-bold mb-5'>{bal.leave_type_name}</p>
                        <div className='flex items-baseline gap-5'>
                            <h3 className='text-2xl font-bold text-org-blue-primary'>{bal.available_days}</h3>
                            <span className='text-xs text-gray-500'>days available</span>
                        </div>
                        <div className='mt-10 pt-10 border-t border-dashed border-gray-200 text-xs flex justify-between'>
                            <span>Allocated: {bal.allocated_days}</span>
                            <span>Used: {bal.used_days}</span>
                        </div>
                        <div className='mt-6 flex items-center justify-between text-xs'>
                            <span className='text-text-secondary'>Total Allocated</span>
                            <span className='font-semibold text-primary'>{bal.max_days_per_year} days</span>
                        </div>
                    </div>
                ))}
            </div>

            {/* MAIN TABLE */}
            <div className='bg-bg-dark rounded shadow-sm p-20 border border-border'>
                <div className='flex justify-between items-center mb-20'>
                    <h2 className='text-lg font-bold'>Leave Requests</h2>
                    <div className='flex items-center gap-10'>
                        <ReportDownloadButton
                            label='Download Report'
                            orgId={orgId}
                            canPickEmployee={isHRForReports}
                            employees={reportEmployees}
                            currentEmployeeId={currentEmployeeId}
                            allOptionLabel='Whole Organization'
                            selfOptionLabel='My Leave'
                            canFilterStatus
                            isPending={downloadLeaveReport.isPending}
                            onDownload={(params) => downloadLeaveReport.mutate(params)}
                        />
                        <button onClick={() => setAddLeaveModal(true)} className='btn btn-primary'>
                            Apply for Leave
                        </button>
                    </div>
                </div>

                <ReactDataTable
                    columns={columns}
                    data={filteredItems}
                    pagination
                    paginationResetDefaultPage={resetPaginationToggle}
                    subHeader
                    subHeaderComponent={subHeaderComponent}
                    highlightOnHover
                    persistTableHead
                />
            </div>

            {/* MODALS */}
            <AddLeaveModal
                isOpen={addLeaveModal}
                onClose={() => setAddLeaveModal(false)}
                leaveTypes={leaveTypes}
                selectedLeaveType={selectedLeaveType}
                setSelectedLeaveType={setSelectedLeaveType}
                fromDate={fromDate} setFromDate={setFromDate}
                toDate={toDate} setToDate={setToDate}
                totalDays={totalDays}
                leaveReason={leaveReason} setLeaveReason={setLeaveReason}
                onSubmit={handleSubmitLeave}
                isSubmitting={createLeaveRequest.isPending}

            />

            {/* Generic Action Modal (Confirm/Approve/Reject) */}
            {actionModal.isOpen && (
                <div className='fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm'>
                    <div className='bg-bg-dark border border-border rounded-lg shadow-lg w-full max-w-md p-20 animate-fade-in'>
                        <h3 className='text-lg font-bold mb-10 capitalize text-text-primary'>{actionModal.type} Request</h3>
                        <p className='text-sm text-text-secondary mb-20'>
                            Are you sure you want to {actionModal.type} the leave request for
                            <span className='font-bold text-text-primary'> {actionModal.request?.employeeName}</span>?
                        </p>

                        {actionModal.type === 'reject' && (
                            <textarea
                                className='w-full border border-border bg-bg-light text-text-primary rounded p-10 text-sm mb-20 focus:border-org-blue-primary outline-none'
                                rows={3}
                                placeholder="Reason for rejection..."
                                value={actionComments}
                                onChange={e => setActionComments(e.target.value)}
                            />
                        )}

                        <div className='flex justify-end gap-10'>
                            <button onClick={() => setActionModal({ isOpen: false, type: '', request: null })} className='btn btn-secondary'>
                                Cancel
                            </button>
                            <button
                                onClick={handleActionSubmit}
                                className={`btn ${actionModal.type === 'reject' ? 'btn-danger' : 'btn-primary'} disabled:opacity-60 disabled:cursor-not-allowed w-full sm:w-auto`}
                            >
                                {actionModal.type === 'confirm' ? 'Confirm' : actionModal.type === 'approve' ? 'Approve' : 'Reject'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <ViewLeaveDetailsModal
                isOpen={viewDetailsModal}
                onClose={() => setViewDetailsModal(false)}
                selectedLeave={selectedLeave}
                formatDate={formatDate}
                getStatusBadge={getStatusBadge}
            />

        </div>
    )
}
